/**
 * Initializes scroll-based reveal animations for elements matching a given selector.
 *
 * Adds a specified CSS class to elements when they enter the viewport based on a customizable threshold.
 * Useful for triggering entry animations such as fade-in, slide-up, etc., as users scroll the page.
 *
 * @param {Object} options - Configuration options for the scroll animation.
 * @param {string} [options.selector='.js-scroll'] - CSS selector for target elements.
 * @param {string} [options.visibleClass='is-visible'] - Class to add when element becomes visible.
 * @param {number} [options.threshold=1.4] - Viewport threshold divisor to trigger animation earlier or later.
 * @param {boolean} [options.once=true] - Whether to run the animation only once per element.
 *
 * NOTE to use:
 *
 * // Default Animation
 * scrollEntryAnimation();
 *
 * OR
 *
 * // With another selector and class
 * scrollEntryAnimation({
 *  selector: '.fade-in-on-scroll',
 *  visibleClass: 'fade-in',
 *  threshold: 1.2,
 * });
 *
 * OR
 *
 * // For continuous animation (does not remove listener)
 * scrollEntryAnimation({
 *  selector: '.repeat-animation',
 *  visibleClass: 'is-visible',
 *  threshold: 1.1,
 *  once: false,
 * });
 *
 */

export function scrollEntryAnimation({
  selector = '.animate-entry',
  visibleClass = 'is-visible',
  threshold = 1.4,
  once = true,
} = {}) {
  const scrollElements = document.querySelectorAll(selector);

  const elementInView = (el, divisor) => {
    const elementTop = el.getBoundingClientRect().top;
    return elementTop <= window.innerHeight / divisor;
  };

  const displayScrollElement = (el) => {
    el.classList.add(visibleClass);
  };

  const handlescrollEntryAnimation = () => {
    scrollElements.forEach((el) => {
      if (elementInView(el, threshold) && !el.classList.contains(visibleClass)) {
        displayScrollElement(el);
      }
    });

    if (once) {
      const anyHidden = [...scrollElements].some((el) => !el.classList.contains(visibleClass));
      if (!anyHidden) {
        window.removeEventListener('scroll', debouncedScroll);
      }
    }
  };

  // Debounce (soft)
  let scrollTimeout;
  const debouncedScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(handlescrollEntryAnimation, 10);
  };

  window.addEventListener('scroll', debouncedScroll);

  // Run on load
  handlescrollEntryAnimation();
}
